class ProductCardVariantSelector extends HTMLElement {
  constructor() {
    super();
    this.handleSwatchClick = this.onSwatchClick.bind(this);
    this.handleSizeClick = this.onSizeClick.bind(this);
  }

  connectedCallback() {
    try {
      var jsonEl = this.querySelector(".product-card-variants");
      if (!jsonEl) return;
      var parsed = JSON.parse(jsonEl.textContent);

      // Support both old format (plain array) and new format ({ variants, product_images })
      if (Array.isArray(parsed)) {
        this.variantData = parsed;
        this.productImages = [];
      } else {
        this.variantData = parsed.variants || [];
        this.productImages = parsed.product_images || [];
      }
    } catch (e) {
      console.error(
        "ProductCardVariantSelector: Failed to parse variant JSON",
        e,
      );
      return;
    }

    this.card = this.closest(".product-card");
    if (!this.card) return;

    // Color swatches
    this.swatches = this.querySelectorAll(".product-swatch");
    this.swatches.forEach(
      function (swatch) {
        swatch.addEventListener("click", this.handleSwatchClick);
      }.bind(this),
    );

    // Size buttons (rendered inside .product-image-container, outside this element)
    this.sizeButtons = this.card.querySelectorAll(".product-size-btn");
    this.sizeButtons.forEach(
      function (btn) {
        btn.addEventListener("click", this.handleSizeClick);
      }.bind(this),
    );

    // Auto-select color if a color filter is active in the URL
    this.autoSelectFilteredColor();
  }

  disconnectedCallback() {
    if (this.swatches) {
      this.swatches.forEach(
        function (swatch) {
          swatch.removeEventListener("click", this.handleSwatchClick);
        }.bind(this),
      );
    }
    if (this.sizeButtons) {
      this.sizeButtons.forEach(
        function (btn) {
          btn.removeEventListener("click", this.handleSizeClick);
        }.bind(this),
      );
    }
  }

  /**
   * Find the best matching variant given optional color and size values.
   * Prefers available variants; falls back to unavailable if needed.
   */
  findVariant(colorValue, colorPosition, sizeValue, sizePosition) {
    var colorKey = colorPosition ? "option" + colorPosition : null;
    var sizeKey = sizePosition ? "option" + sizePosition : null;

    // Try to find an available variant matching both options
    var variant = this.variantData.find(function (v) {
      var colorMatch = !colorKey || v[colorKey] === colorValue;
      var sizeMatch = !sizeKey || v[sizeKey] === sizeValue;
      return colorMatch && sizeMatch && v.available;
    });

    // Fallback: match both options but unavailable
    if (!variant) {
      variant = this.variantData.find(function (v) {
        var colorMatch = !colorKey || v[colorKey] === colorValue;
        var sizeMatch = !sizeKey || v[sizeKey] === sizeValue;
        return colorMatch && sizeMatch;
      });
    }

    return variant || null;
  }

  /**
   * Get the currently selected color value from the active swatch.
   */
  getSelectedColor() {
    var activeSwatch = this.querySelector(".product-swatch--active");
    if (activeSwatch) {
      return activeSwatch.dataset.colorValue;
    }
    return null;
  }

  /**
   * Get the color option position from the swatches container.
   */
  getColorPosition() {
    var swatchContainer = this.querySelector(".product-swatches");
    if (swatchContainer) {
      return parseInt(swatchContainer.dataset.optionPosition, 10);
    }
    return null;
  }

  /**
   * Get the currently selected size value from the active size button.
   */
  getSelectedSize() {
    if (!this.card) return null;
    var activeSize = this.card.querySelector(".product-size-btn--active");
    if (activeSize) {
      return activeSize.dataset.sizeValue;
    }
    return null;
  }

  /**
   * Get the size option position from the size selector container.
   */
  getSizePosition() {
    if (!this.card) return null;
    var sizeContainer = this.card.querySelector("[data-size-selector]");
    if (sizeContainer) {
      return parseInt(sizeContainer.dataset.sizeOptionPosition, 10);
    }
    return null;
  }

  /**
   * Find a fallback image from the product's image gallery whose alt text
   * contains the given color name (case-insensitive).
   */
  findImageByColor(colorValue) {
    if (!this.productImages || this.productImages.length === 0) return null;
    var colorLower = (colorValue || "").toLowerCase();
    var match = null;

    // Try exact match first
    for (var i = 0; i < this.productImages.length; i++) {
      var imgAlt = (this.productImages[i].alt || "").toLowerCase();
      if (imgAlt === colorLower) {
        match = this.productImages[i];
        break;
      }
    }

    // Then try contains match
    if (!match) {
      for (var j = 0; j < this.productImages.length; j++) {
        var alt = (this.productImages[j].alt || "").toLowerCase();
        if (alt.indexOf(colorLower) !== -1) {
          match = this.productImages[j];
          break;
        }
      }
    }

    return match;
  }

  onSwatchClick(e) {
    e.preventDefault();
    e.stopPropagation();

    if (!this.card) return;

    var colorValue = e.currentTarget.dataset.colorValue;
    var colorPosition = this.getColorPosition();
    var sizeValue = this.getSelectedSize();
    var sizePosition = this.getSizePosition();

    var variant = this.findVariant(
      colorValue,
      colorPosition,
      sizeValue,
      sizePosition,
    );
    if (!variant) return;

    // Update active swatch
    this.swatches.forEach(function (s) {
      s.classList.remove("product-swatch--active");
    });
    e.currentTarget.classList.add("product-swatch--active");

    this.updateCard(variant, colorValue);
  }

  onSizeClick(e) {
    e.preventDefault();
    e.stopPropagation();

    if (!this.card) return;

    var sizeValue = e.currentTarget.dataset.sizeValue;
    var sizePosition = this.getSizePosition();
    var colorValue = this.getSelectedColor();
    var colorPosition = this.getColorPosition();

    var variant = this.findVariant(
      colorValue,
      colorPosition,
      sizeValue,
      sizePosition,
    );
    if (!variant) return;

    // Update active size button
    this.sizeButtons.forEach(function (s) {
      s.classList.remove("product-size-btn--active");
    });
    e.currentTarget.classList.add("product-size-btn--active");

    this.updateCard(variant, colorValue);
  }

  /**
   * Apply the selected variant to all card elements (image, price, URLs, ATC, etc.)
   * @param {object} variant  - The matched variant object
   * @param {string} colorValue - The selected color label (used for fallback image lookup)
   */
  updateCard(variant, colorValue) {
    var card = this.card;

    // 1. Update variant ID hidden input
    var variantInput = card.querySelector('input[name="id"]');
    if (variantInput) variantInput.value = variant.id;

    // 2. Update product URLs
    var imageLink = card.querySelector(".product-card__image-link");
    var titleLink = card.querySelector(".product-card__title-link");
    if (imageLink) {
      var baseUrl = imageLink.dataset.baseUrl;
      var variantUrl = baseUrl + "?variant=" + variant.id;
      imageLink.href = variantUrl;
      if (titleLink) titleLink.href = variantUrl;
    }

    // 3. Update primary image
    // Priority: variant.featured_image → fallback by color in alt text → no change
    var imageData = variant.featured_image;
    if (!imageData && colorValue) {
      imageData = this.findImageByColor(colorValue);
    }

    if (imageData) {
      var img = card.querySelector(
        ".product-image-container img.responsive-image",
      );
      if (img) {
        img.src = imageData.src;
        img.setAttribute("data-src", imageData.data_src);
        img.setAttribute("data-srcset", imageData.srcset);
        img.setAttribute("alt", imageData.alt);
        img.setAttribute("width", imageData.width);
        img.setAttribute("height", imageData.height);
        // Re-trigger lazysizes
        img.classList.remove("lazyloaded");
        img.classList.add("lazyload");
      }
    }

    // 4. Update sold-out state
    var overlay = card.querySelector("[data-sold-out-overlay]");
    var badge = card.querySelector("[data-sold-out-badge]");
    if (overlay) overlay.style.display = variant.available ? "none" : "";
    if (badge) badge.style.display = variant.available ? "none" : "";

    // 5. Update price
    var priceContainer = card.querySelector("[data-price-container]");
    if (priceContainer) {
      var html = "";
      if (variant.compare_at_price_formatted) {
        html += '<div class="product-price-container-content">';
        html +=
          '<span class="product-price" style="color: var(--color-price-foreground); font-weight: 500;">' +
          variant.price_formatted +
          "</span>";
        html +=
          '<s class="compare-at-price">' +
          variant.compare_at_price_formatted +
          "</s>";
        html += "</div>";
      } else {
        html += '<div class="product-price-container-content">';
        html +=
          '<span class="product-price">' + variant.price_formatted + "</span>";
        html += "</div>";
      }

      var showPromo = priceContainer.dataset.showPromo === "true";
      if (showPromo && variant.discount_percentage > 0) {
        html +=
          '<div class="product-discounted-promo"><span>' +
          variant.discount_percentage +
          "% off</span></div>";
      } else if (showPromo) {
        html += '<div class="product-discounted-promo"></div>';
      }

      priceContainer.innerHTML = html;
    }

    // 6. Update add-to-cart button
    var atcButton = card.querySelector("add-to-cart button");
    if (atcButton) {
      var atcText = atcButton.querySelector(".atc-button__text");
      if (variant.available) {
        atcButton.disabled = false;
        if (atcText) atcText.textContent = "ADD TO CART";
      } else {
        atcButton.disabled = true;
        if (atcText) atcText.textContent = "SOLD OUT";
      }
    }
  }

  /**
   * Reads active color filters from the URL and auto-selects the matching
   * swatch on this card, triggering the image / price / URL update.
   *
   * Shopify filter URL param formats vary, e.g.:
   *   filter.v.option.color=Black
   *   filter.p.m.custom.color=Black
   *   filter.p.m.product.color=Black
   * We match any param key that contains 'color' or 'colour' (case-insensitive).
   */
  autoSelectFilteredColor() {
    if (!this.swatches || this.swatches.length === 0) return;

    var params = new URLSearchParams(window.location.search);
    var activeColor = null;

    params.forEach(function (value, key) {
      if (!activeColor) {
        var keyLower = key.toLowerCase();
        if (
          keyLower.indexOf("color") !== -1 ||
          keyLower.indexOf("colour") !== -1
        ) {
          activeColor = value;
        }
      }
    });

    if (!activeColor) return;

    var activeColorLower = activeColor.toLowerCase();

    // Find the swatch whose data-color-value matches (case-insensitive)
    var matchingSwatch = null;
    this.swatches.forEach(function (swatch) {
      if (!matchingSwatch) {
        var swatchVal = (swatch.dataset.colorValue || "").toLowerCase();
        if (swatchVal === activeColorLower) {
          matchingSwatch = swatch;
        }
      }
    });

    if (!matchingSwatch) return;

    var colorValue = matchingSwatch.dataset.colorValue;
    var colorPosition = this.getColorPosition();
    var sizeValue = this.getSelectedSize();
    var sizePosition = this.getSizePosition();

    var variant = this.findVariant(
      colorValue,
      colorPosition,
      sizeValue,
      sizePosition,
    );
    if (!variant) return;

    // Mark the correct swatch as active
    this.swatches.forEach(function (s) {
      s.classList.remove("product-swatch--active");
    });
    matchingSwatch.classList.add("product-swatch--active");

    this.updateCard(variant, colorValue);
  }
}

customElements.define(
  "product-card-variant-selector",
  ProductCardVariantSelector,
);
